import { CalendarDate } from '@internationalized/date';
import classNames from 'classnames';
import { ComponentProps, ReactNode } from 'react';
import { FormattedDate } from 'react-intl';

import guessUserTimeZone from '../../helpers/date/guessUserTimeZone';
import CleanButton from '../CleanButton/CleanButton';
import HorizontalSlider from '../HorizontalSlider/HorizontalSlider';
import FadeConfiguration from '../HorizontalSlider/types/FadeConfiguration';
import ListPicker, { ListPickerProps } from '../ListPicker/ListPicker';
import Paragraph from '../Paragraph/Paragraph';
import Row from '../Row/Row';
import SVG from '../SVG/SVG';
import Stack from '../Stack/Stack';
import ToggleButton from '../ToggleButton/ToggleButton';

import MonthLabels from './components/MonthLabels/MonthLabels';
import calendar from './graphics/calendar.svg?raw';

import styles from './NearestDatesPicker.module.css';

export type DateOption = {
    date: CalendarDate;
    value: string;
    disabled?: boolean;
};

const dateToOption = (
    date: CalendarDate,
    minimum?: CalendarDate
): DateOption => ({
    date: date,
    value: date.toString(),
    disabled: minimum && date < minimum,
});

const generateDates = (
    reference: CalendarDate,
    datesBefore: number,
    datesAfter: number,
    minimum?: CalendarDate
) => {
    const dates = [];
    for (let i = datesBefore; i > 0; i--) {
        dates.push(dateToOption(reference.subtract({ days: i }), minimum));
    }

    dates.push(dateToOption(reference, minimum));

    for (let i = 1; i <= datesAfter; i++) {
        dates.push(dateToOption(reference.add({ days: i }), minimum));
    }

    return dates;
};

const buttonWidth = 48;
const buttonSpacing = 6; // --spacing-xsmall

const fadeConfig: FadeConfiguration = {
    placement: 'both',
    offsetY: 16,
    color: '#ffffff',
};

type NearestDatesPickerProps = {
    startDate: CalendarDate;
    value?: CalendarDate;
    defaultValue?: CalendarDate;
    minimum?: CalendarDate;
    onMoreDatesRequested?: () => void;
    onChange?: (date?: CalendarDate) => void;
    numberOfDates?: number;
    moreLabel: ReactNode;
    scrollRef?: ComponentProps<typeof HorizontalSlider>['scrollRef'];
} & Omit<
    ListPickerProps<DateOption, 'value'>,
    | 'options'
    | 'renderOption'
    | 'valueProperty'
    | 'value'
    | 'inline'
    | 'defaultValue'
    | 'onChange'
>;

const NearestDatesPicker = ({
    startDate,
    value,
    defaultValue,
    minimum,
    numberOfDates = 21,
    onMoreDatesRequested,
    onChange,
    moreLabel,
    scrollRef,
    ...pickerOptions
}: NearestDatesPickerProps) => {
    const dates = generateDates(startDate, 0, numberOfDates, minimum);
    const userTimeZone = guessUserTimeZone();

    return (
        <div
            className={classNames(styles.Root, {
                [styles.hasMoreButton]: onMoreDatesRequested,
            })}
        >
            <HorizontalSlider
                fade={fadeConfig}
                mode="scroll"
                scrollRef={scrollRef}
            >
                <MonthLabels
                    dates={dates}
                    timeZone={userTimeZone}
                    buttonWidth={buttonWidth}
                    buttonSpacing={buttonSpacing}
                />

                <ListPicker
                    {...pickerOptions}
                    inline
                    defaultValue={defaultValue?.toString()}
                    onChange={(value, option) =>
                        onChange && onChange(value ? option.date : undefined)
                    }
                    value={value?.toString()}
                    options={dates}
                    renderOption={({ option, onSelect, selected }) => (
                        <ToggleButton
                            toggled={selected}
                            onClick={onSelect}
                            disabled={option.disabled}
                            className={styles.Button}
                            style={{ width: buttonWidth }}
                        >
                            <Stack space="none">
                                <Paragraph
                                    size="small"
                                    align="center"
                                    hideOverflow
                                >
                                    <FormattedDate
                                        value={option.date.toDate(userTimeZone)}
                                        weekday="short"
                                    />
                                </Paragraph>
                                <Paragraph bold align="center">
                                    {option.date.day}
                                </Paragraph>
                            </Stack>
                        </ToggleButton>
                    )}
                    valueProperty="value"
                />
            </HorizontalSlider>

            {onMoreDatesRequested && (
                <CleanButton
                    onClick={onMoreDatesRequested}
                    className={styles.MoreButton}
                    fullWidth
                >
                    <Row horizontal="center">
                        <SVG src={calendar} className={styles.MoreIcon} />
                    </Row>
                    <Row horizontal="center">
                        <Paragraph size="small">{moreLabel}</Paragraph>
                    </Row>
                </CleanButton>
            )}
        </div>
    );
};

export default NearestDatesPicker;
